import { ApolloError, gql, useQuery } from '@apollo/client';
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
} from '@apollo/orbit';
import React, { useState } from 'react';

import { Loading } from 'src/components/common/loading/Loading';
import { NotFound } from 'src/components/common/notFound/NotFound';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { useCurrentPlan } from 'src/hooks/currentPlanV2Migration';
import { useCurrentAccountId } from 'src/hooks/useCurrentAccountId';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

import { GraphRef } from '../../hooks/useGraphRef';

import { ApiKeysCard } from './apiKeysCard/ApiKeysCard';

const APIKeysPageQuery = gql`
  query GraphSettings__APIKeysPage($graphId: ID!) {
    service(id: $graphId) {
      id
      title
      account {
        id
      }
      apiKeys {
        id
        keyName
        role
        createdAt
        createdBy {
          id
          ... on User {
            fullName
            avatarUrl
            githubUsername
          }
        }
      }
    }
  }
`;

export const APIKeysPage = ({ graphRef }: { graphRef: GraphRef }) => {
  const [actionError, setActionError] = useState<ApolloError | null>(null);

  const { data, loading, error } = useQuery<
    GraphQLTypes.GraphSettings__APIKeysPage,
    GraphQLTypes.GraphSettings__APIKeysPageVariables
  >(APIKeysPageQuery, {
    variables: {
      graphId: graphRef.graphId,
    },
    errorPolicy: 'all',
  });
  const [currentAccountId] = useCurrentAccountId();
  const currentPlan = useCurrentPlan({
    skip: !currentAccountId,
    accountId: currentAccountId as string,
  });

  return loading || currentPlan.loading ? (
    <Loading />
  ) : error || currentPlan.error ? (
    <ErrorMessage error={error ?? currentPlan.error} />
  ) : !data?.service || !data.service?.account ? (
    <NotFound />
  ) : (
    <>
      <ApiKeysCard
        graph={data.service}
        userRoles={currentPlan.currentPlan?.userRoles || false}
        setError={setActionError}
      />
      {actionError && (
        <Modal isOpen size="lg" onClose={() => setActionError(null)}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>
              <ModalTitle>Error</ModalTitle>
            </ModalHeader>
            <ModalBody>
              <div className="w-full rounded bg-red-lightest px-4 py-3 text-base leading-normal text-red-dark">
                <div>An error occurred: {actionError.message}</div>
                <div>Please try again</div>
              </div>
            </ModalBody>
            <ModalFooter>
              <Button variant="primary" onClick={() => setActionError(null)}>
                Close
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </>
  );
};
